/**
 * Continuous stage recorder — §1: no pause/resume, no splicing; one continuous file per
 * take. A discarded take (§11 abort) never contributes bytes to an uploaded file.
 */

const MIME_CANDIDATES = [
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=h264',
  'video/webm;codecs=vp9',
  'video/webm',
];

export function pickRecorderMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return null;
}

/** Container mime for the upload manifest (strips codec parameters). */
export function uploadMimeType(recorderMime: string): 'video/mp4' | 'video/webm' {
  return recorderMime.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';
}

export class StageRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mime: string;
  /** video.currentTime (media time, ms) at the moment recording started — §19 hints
   * and §20 stop decisions are made relative to this offset. */
  startMediaTimeMs = 0;

  constructor(private stream: MediaStream) {
    const mime = pickRecorderMime();
    if (!mime) throw new Error('MediaRecorder unsupported');
    this.mime = mime;
  }

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  get mimeType(): string {
    return this.mime;
  }

  start(currentMediaTimeMs: number): void {
    this.chunks = [];
    this.startMediaTimeMs = currentMediaTimeMs;
    this.recorder = new MediaRecorder(this.stream, {
      mimeType: this.mime,
      videoBitsPerSecond: 8_000_000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start(1000);
  }

  stop(): Promise<Blob> {
    const rec = this.recorder;
    if (!rec || rec.state === 'inactive') return Promise.resolve(new Blob(this.chunks, { type: this.mime }));
    return new Promise((resolve, reject) => {
      rec.onstop = () => resolve(new Blob(this.chunks, { type: this.mime }));
      rec.onerror = () => reject(new Error('Recorder error'));
      rec.stop();
    });
  }

  /** §11 — discard the failed take entirely. */
  cancel(): void {
    const rec = this.recorder;
    this.chunks = [];
    if (rec && rec.state !== 'inactive') {
      rec.ondataavailable = null;
      rec.onstop = null;
      rec.stop();
    }
    this.recorder = null;
  }
}
